import {noop} from '@benchling/js-utils';

import DocumentStore from 'notebook/documents/DocumentStore';
import {
  ProcedurePageContext,
  BlockingSaveObjectSource,
  BlockingSaveOperation,
} from 'procedures/ProcedurePageContext';
import legacyRunModal from 'ui-library/legacyRunModal';

import {MethodEditorTab} from './method-config/MethodConfigConstants';
import ProcedureConfirmExitWithUnsavedChangesModal, {
  ProcedureConfirmExitWithUnsavedChangesModalProps,
} from './modals/ProcedureConfirmExitWithUnsavedChangesModal';
import {ProcedureValidationContext} from './validation/ProcedureValidationContext';
import {findParameterTableValidationErrors} from './validation/procedureValidationUtils';

/**
 * Checks if there are unsaved changes in a method editor tab and prompts the user to save their
 * changes before leaving the view. This should be called in flows where the user is able to navigate
 * away from the tab while still editing (i.e., isEditingMethodTab).
 *
 * This is only relevant for legacy procedures. For PoW procedures, see useFlowchartSidepanelManager.

 * @param callbackFn - Callback is called in every path except when the user cancels the modal by clicking the "X". It has
 * a single boolean parameter didSave that indicates whether the user saved their changes or not.
 * * @returns A function that returns a promise that resolves to a boolean indicating whether the user took action that allows them to leave the method tab.
 */
export default function useLegacyUnsavedTabChangesCheck(
  currentMethodEditorTab?: MethodEditorTab
): (callbackFn: (didSave: boolean) => void) => void {
  const {
    legacyIsEditingMethodTab,
    setLegacyIsEditingMethodTab,
    legacyIsProcedureDirty,
    legacySelectedMethodCid,
    legacySaveImmediately,
    resetLegacyDraftFlowchartChanges,
  } = ProcedurePageContext.useContext();
  const {procedureValidationErrors, anyTabValidationErrors} = ProcedureValidationContext.useContext();

  return (callbackFn: (didSave: boolean) => void) => {
    const isTabClean = !legacyIsEditingMethodTab || !legacyIsProcedureDirty;
    const noMethodIsSelected = legacySelectedMethodCid == null;

    if (isTabClean || noMethodIsSelected) {
      setLegacyIsEditingMethodTab(false);
      callbackFn(false);
      return;
    }

    const parameterTableValidationErrors = findParameterTableValidationErrors(
      procedureValidationErrors,
      legacySelectedMethodCid
    );
    legacyRunModal<ProcedureConfirmExitWithUnsavedChangesModalProps>(
      ProcedureConfirmExitWithUnsavedChangesModal,
      {
        onRequestClose: noop,
        onRequestLeaveWithoutSaving: () => {
          resetLegacyDraftFlowchartChanges();
          setLegacyIsEditingMethodTab(false);
          setTimeout(() => {
            callbackFn(false);
          }, 0);
        },
        onRequestSubmit: async () => {
          // This call requires an immediate API call, with an overlay
          await legacySaveImmediately(
            () => ({
              blockingSaveObjectSource: BlockingSaveObjectSource.Procedure,
              blockingSaveOperation: BlockingSaveOperation.SaveUnsubmittedOnExit,
            }),
            'Submitting changes...'
          );
          // When saving Parameter Definitions, we need to clear the saved documents in memory so
          // that the user fetches them with any new changes applied
          DocumentStore.resetState();
          setLegacyIsEditingMethodTab(false);
          setTimeout(() => {
            callbackFn(true);
          }, 0);
        },
        isEditingMethodTab: true,
        currentMethodEditorTab,
        anyErrorsInTables: parameterTableValidationErrors.length > 0 || anyTabValidationErrors,
      }
    );
  };
}
